import type {
  CastBlockContent,
  CastInlineContent,
  CastPrimaryStyle,
  CastRoot,
  CastSection,
  CastTextStyle,
} from '../types';

export type PrimaryStyleRun = { text: string; style?: CastTextStyle };

/**
 * Reduces a paragraph's text runs to its dominant typography, deciding each
 * property independently by character-weighted majority. See CAST_README.md
 * for the rules:
 * - whitespace-only runs are ignored;
 * - booleans are sparse — set to `true` only when most characters have them;
 * - valued fields (fontFamily/fontSize) take the value covering the most
 *   characters among runs that specify it, ties broken by first-encountered,
 *   and are omitted when no run specifies one;
 * - an empty result is `undefined`, never `{}`.
 *
 * `strikethrough` is intentionally never read — it's excluded from
 * CastPrimaryStyle.
 */
export function computePrimaryStyle(runs: PrimaryStyleRun[]): CastPrimaryStyle | undefined {
  const counted = runs.filter(run => run.text.trim() !== '');
  const totalChars = counted.reduce((sum, run) => sum + run.text.length, 0);
  if (totalChars === 0) return undefined;

  const primary: CastPrimaryStyle = {};
  if (majorityHasFlag(counted, totalChars, style => style.bold)) primary.bold = true;
  if (majorityHasFlag(counted, totalChars, style => style.italic)) primary.italic = true;
  if (majorityHasFlag(counted, totalChars, style => style.underline)) primary.underline = true;

  const fontFamily = dominantValue(counted, style => style.fontFamily);
  if (fontFamily !== undefined) primary.fontFamily = fontFamily;
  const fontSize = dominantValue(counted, style => style.fontSize);
  if (fontSize !== undefined) primary.fontSize = fontSize;

  return Object.keys(primary).length > 0 ? primary : undefined;
}

/**
 * Convenience wrapper for callers that already hold built Cast inline content
 * (e.g. the PDF extractor, whose runs carry the full style). Pulls the text
 * runs out and delegates to computePrimaryStyle.
 */
export function computePrimaryStyleFromInline(
  children: CastInlineContent[]
): CastPrimaryStyle | undefined {
  const runs: PrimaryStyleRun[] = [];
  collectInlineRuns(children, runs);
  return computePrimaryStyle(runs);
}

/** Collects text runs from inline content, descending into links. */
function collectInlineRuns(children: CastInlineContent[], runs: PrimaryStyleRun[]): void {
  for (const node of children) {
    if (node.type === 'text') runs.push({ style: node.style, text: node.value });
    else if (node.type === 'link') collectInlineRuns(node.children, runs);
  }
}

/**
 * The section's dominant typography: the most common paragraph `primaryStyle`
 * among all paragraphs in the section (including those nested in tables),
 * counted one vote per paragraph — no character weighting. Ties go to the
 * first-encountered style. Paragraphs without a primaryStyle don't vote;
 * returns undefined when none do.
 */
export function computeSectionPrimaryStyle(section: CastSection): CastPrimaryStyle | undefined {
  const styles: CastPrimaryStyle[] = [];
  for (const block of section.children) collectBlockPrimaryStyles(block, styles);
  if (styles.length === 0) return undefined;

  const tally = new Map<string, { count: number; style: CastPrimaryStyle }>();
  for (const style of styles) {
    const key = primaryStyleKey(style);
    const entry = tally.get(key);
    if (entry) entry.count += 1;
    else tally.set(key, { count: 1, style });
  }

  let best: { count: number; style: CastPrimaryStyle } | undefined;
  for (const entry of tally.values()) {
    if (!best || entry.count > best.count) best = entry;
  }
  return best ? { ...best.style } : undefined;
}

/** Sets `primaryStyle` on each present section of the root, mutating in place. */
export function assignSectionPrimaryStyles(root: CastRoot): void {
  for (const section of root.children) {
    const primaryStyle = computeSectionPrimaryStyle(section);
    if (primaryStyle) section.primaryStyle = primaryStyle;
  }
}

function collectBlockPrimaryStyles(block: CastBlockContent, out: CastPrimaryStyle[]): void {
  if (block.type === 'paragraph') {
    if (block.primaryStyle) out.push(block.primaryStyle);
    return;
  }
  for (const row of block.children) {
    for (const cell of row.children) {
      for (const inner of cell.children) collectBlockPrimaryStyles(inner, out);
    }
  }
}

/** Order-independent key for grouping identical primaryStyle objects. */
function primaryStyleKey(style: CastPrimaryStyle): string {
  return JSON.stringify([
    style.bold,
    style.fontFamily,
    style.fontSize,
    style.italic,
    style.underline,
  ]);
}

function majorityHasFlag(
  runs: PrimaryStyleRun[],
  totalChars: number,
  pick: (style: CastTextStyle) => boolean | undefined
): boolean {
  let flaggedChars = 0;
  for (const run of runs) {
    if (run.style && pick(run.style) === true) flaggedChars += run.text.length;
  }
  return flaggedChars * 2 > totalChars;
}

function dominantValue<T extends number | string>(
  runs: PrimaryStyleRun[],
  pick: (style: CastTextStyle) => T | undefined
): T | undefined {
  const charsByValue = new Map<T, number>();
  for (const run of runs) {
    const value = run.style ? pick(run.style) : undefined;
    if (value === undefined) continue;
    charsByValue.set(value, (charsByValue.get(value) ?? 0) + run.text.length);
  }

  let best: T | undefined;
  let bestChars = 0;
  for (const [value, chars] of charsByValue) {
    if (chars > bestChars) {
      best = value;
      bestChars = chars;
    }
  }
  return best;
}
